import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import { Document, Packer, Paragraph, TextRun, ImageRun } from "docx";
import html2canvas from "html2canvas";
import Mlogo from "../assets/Mlogo.png";

// ---------------- Helpers ----------------
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Format with currency & Indian commas
const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(toNum(value));
};
// Format date into dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr; // fallback if it's not a valid date
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

// Returns discount percent (0–100).
const getDiscountPercent = (item) => {
  let d = toNum(item.discount);
  if (d > 0 && d <= 1) d = d * 100;
  if (d > 0) return +d.toFixed(2);

  const qty = toNum(item.qty);
  const rate = toNum(item.rate);
  const amount = toNum(item.amount);
  const gross = qty * rate;
  if (gross <= 0) return 0;

  const pct = ((gross - amount) / gross) * 100;
  return +Math.max(0, Math.min(100, pct)).toFixed(2);
};

// ✅ Invoice Preview Component
function InvoicePreview({ invoiceData }) {
  return (
    <div id="invoice-preview" className="p-6 bg-white shadow rounded-lg">
      {/* Header with Logo */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <img src={Mlogo} alt="Company Logo" className="h-12" />
        </div>
        <div>
          <h2 className="text-xl font-bold">PROFORMA INVOICE</h2>
          <p className="text-sm text-gray-600">Marker Electronic Equipments</p>
        </div>
      </div>
      <div className="flex justify-between mb-4 border-b pb-4">
        <div>
          <p className="font-semibold">From:</p>
          <p>MARKER ELECTRONIC EQUIPMENTS</p>
          <p>Plot no 91/1(D), Satpur MIDC, Nashik 422007. (India)</p>
          <p>+91 755 933 6283</p>
          <p>GST NO : 27ABMPB9440N2ZN</p>
        </div>
        <div className="text-right">
          <p>
            <strong>Invoice No:</strong> {invoiceData.invoiceNo}
          </p>
          <p>
            <strong>Date:</strong> {formatDate(invoiceData.invoiceDate)}
          </p>
        </div>
      </div>
      <div className="text-right  mb-6">
        <p className="font-semibold">Bill To:</p>
        <p>{invoiceData.billTo || "N/A"}</p>
      </div>

      <table className="w-full border-collapse border">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-2 py-1">Sr no</th>
            <th className="border px-2 py-1">Particular</th>
            <th className="border px-2 py-1">Qty</th>
            <th className="border px-2 py-1">Rate</th>
            <th className="border px-2 py-1">Discount (%)</th>
            <th className="border px-2 py-1">Amount</th>
          </tr>
        </thead>
        <tbody>
          {invoiceData.items.map((item, i) => (
            <tr key={i}>
              <td className="border px-2 py-1 text-center">{i + 1}</td>
              <td className="border px-2 py-1 text-center">
                {item.particular}
              </td>
              <td className="border px-2 py-1 text-center">{item.qty}</td>
              <td className="border px-2 py-1 text-center">
                {formatCurrency(item.rate)}
              </td>
              <td className="border px-2 py-1 text-center">
                {getDiscountPercent(item)}
              </td>
              <td className="border px-2 py-1 text-center">
                {formatCurrency(item.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      {/* Totals */}
      <div className="mt-4 w-full max-w-sm ml-auto border rounded">
        <div className="flex justify-between border-b px-3 py-2">
          <span>Subtotal</span>
          <span>{formatCurrency(invoiceData.subtotal)}</span>
        </div>
        <div className="flex justify-between border-b px-3 py-2">
          <span>GST @18%</span>
          <span>{formatCurrency(invoiceData.gst)}</span>
        </div>
        <div className="flex justify-between border-b px-3 py-2">
          <span>Transport</span>
          <span>{formatCurrency(invoiceData.transport)}</span>
        </div>
        <div className="flex justify-between bg-gray-100 px-3 py-2 font-bold text-lg">
          <span>Grand Total</span>
          <span>{formatCurrency(invoiceData.grandTotal)}</span>
        </div>
      </div>

      {/* Bank Details */}
      <div className="mt-6">
        <h3 className="font-semibold mb-2">OUR BANK DETAILS</h3>
        <p>Bank: HDFC Bank</p>
        <p>A/C No: 1234567890</p>
        <p>IFSC: HDFC0001234</p>
        <p>Branch: Nashik</p>
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-between">
        <p className="font-semibold">{invoiceData.signatory}</p>
      </div>
    </div>
  );
}

export default function PreviewPage() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state) return <p>No data to preview</p>;
  const { format, invoiceData } = state;

  // ✅ Full Invoice Download Logic
  const handleDownload = async () => {
    const invoiceElement = document.getElementById("invoice-preview");

    if (format === "pdf") {
      const canvas = await html2canvas(invoiceElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      pdf.save(`invoice-${invoiceData.invoiceNo}.pdf`);
    }

    if (format === "word") {
      const canvas = await html2canvas(invoiceElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const doc = new Document({
        sections: [
          {
            children: [
              new Paragraph({
                children: [
                  new ImageRun({
                    data: imgData.split(",")[1], // ✅ pass Base64
                    transformation: {
                      width: 600, // keep proportional
                      height: (canvas.height / canvas.width) * 600,
                    },
                  }),
                ],
              }),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      FileSaver.saveAs(blob, `invoice-${invoiceData.invoiceNo}.docx`);
    }

    if (format === "excel") {
      const worksheet = XLSX.utils.json_to_sheet(
        invoiceData.items.map((item, i) => ({
          "#": i + 1,
          Particular: item.particular,
          Qty: item.qty,
          Rate: item.rate,
          "Discount (%)": getDiscountPercent(item),
          Amount: item.amount,
        }))
      );
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: 4 }); // 4th column = Discount
        if (!worksheet[cellRef]) continue;
        worksheet[cellRef].s = {
          alignment: { horizontal: "center" },
        };
      }
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [
          [],
          ["Subtotal", invoiceData.subtotal],
          ["GST", invoiceData.gst],
          ["Transport", invoiceData.transport],
          ["Grand Total", invoiceData.grandTotal],
          [],
          ["OUR BANK DETAILS"],
          ["Bank", "HDFC Bank"],
          ["A/C No", "1234567890"],
          ["IFSC", "HDFC0001234"],
          ["Branch", "Nashik"],
        ],
        { origin: -1 }
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoice");
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });
      FileSaver.saveAs(data, `invoice-${invoiceData.invoiceNo}.xlsx`);
    }

    navigate("/"); // back home
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-6">
      <h1 className="text-2xl font-bold mb-4">Review Your Invoice</h1>

      {/* Invoice Preview */}
      <div className="border bg-gray-100 shadow-lg p-6 w-full max-w-4xl rounded-lg">
        <InvoicePreview invoiceData={invoiceData} />
      </div>

      {/* Buttons */}
      <div className="flex gap-4 mt-6">
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded"
        >
          Cancel
        </button>
        <button
          onClick={handleDownload}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
        >
          Confirm & Download
        </button>
      </div>
    </div>
  );
}
